import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { lazy, Suspense } from "react";

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const FormDetailPage = lazy(() => import("@/pages/FormDetailPage"));
const CreateFormPage = lazy(() => import("@/pages/CreateFormPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const HIPAAApplyPage = lazy(() => import("@/pages/HIPAAApplyPage"));
const SubscriptionSuccessPage = lazy(() => import("@/pages/SubscriptionSuccessPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const DocsPage = lazy(() => import("@/pages/DocsPage"));
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
  </div>
);

const VerificationBanner = () => {
  const { user } = useAuth();
  if (!user || user.email_verified === true) return null;
  return (
    <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-3 text-center" data-testid="verification-banner">
      <p className="text-sm text-yellow-400">
        Please verify your email address to unlock all features. Check your inbox for the verification link.
      </p>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  return (
    <>
      <VerificationBanner />
      {children}
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-background">
        <div className="noise-overlay" />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/verify" element={<VerifyEmailPage />} />
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
              <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/forms/new" element={<ProtectedRoute><CreateFormPage /></ProtectedRoute>} />
              <Route path="/forms/:formId" element={<ProtectedRoute><FormDetailPage /></ProtectedRoute>} />
              <Route path="/forms/:formId/edit" element={<ProtectedRoute><CreateFormPage /></ProtectedRoute>} />
              <Route path="/hipaa/apply" element={<ProtectedRoute><HIPAAApplyPage /></ProtectedRoute>} />
              <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<Navigate to="/" />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/templates" element={<TemplatesPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
              <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
